import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { CancelButton, SubmitButton } from "../../components/btns";
import { UCWords } from "../../components/resources";
import { errorAlert, successAlert } from "../../components/toastr";
import { billiingUnits } from "../../assets/constants";
import { createRate, updateRate } from "../../resources/api/vehicle-rates";
import { getCategories } from "../../resources/api/vehicle-categories";
import SelectInput from "../../components/select";



const { Control, Label } = Form;

/**
 * @param {Object} props
 * @param {"create"|"edit"} props.action
 * @param {(details: import("../../resources/api/vehicle-rates").VehicleRateObject) => void} props.onUpload
 * @param {{
 *  show: boolean
 *  setShow: React.Dispatch<React.SetStateAction<boolean>>
 * }} props.show
 * @param {{
 *  details: import("../../resources/api/vehicle-rates").VehicleRateObject
 *  setDetails: React.Dispatch<React.SetStateAction<import("../../resources/api/vehicle-rates").VehicleRateObject>>
 * }} props.rateDetails
 */
const VehicleRateForm = ({ onUpload, action = "create", rateDetails, show: toShow }) => {

    const inputref = useRef();

    const { show, setShow } = toShow;
    const [categories_loaded, setCategoriesLoaded] = useState(false);
    const [vehicle_categories, setVehicleCategories] = useState([]);
    const [handlingSubmit, setHandlingSubmit] = useState(false);
    const [validated, setValidated] = useState(false);

    const { details, setDetails } = rateDetails;



    useEffect(() => {

        if (!show) return;

        if (categories_loaded) return;

        //get categories then set categories loaded to true

        getCategories()
            .then(({ categories }) => {
                setVehicleCategories(categories);
                successAlert("Loaded vehicle categories");
            })
            .catch(e => errorAlert(`Failed to load vehicle categories... ${e}`))
            .finally(() => setCategoriesLoaded(true));

    }, [show, categories_loaded])



    /**
     * Handles the submission of the form
     * @param {React.MouseEvent} e 
     */
    const handleSubmit = e => {

        e.preventDefault();
        const form = inputref.current;

        if (!form.checkValidity()) {
            setValidated(true);
            errorAlert("Some errors have been detected in the form. These have been highlighted for you.", "Invalid Fields");
            return;
        }

        setHandlingSubmit(true);
        setValidated(false);

        let promise;

        if (action === "create") {
            promise = createRate({
                ...details,
                includesDriver: details.includesDriver ? 1 : 0,
                upCountry: details.upCountry ? 1 : 0,
                includesFuel: details.includesFuel ? 1 : 0,
            });
        } else {
            promise = updateRate({
                ...details,
                includesDriver: details.includesDriver ? 1 : 0,
                upCountry: details.upCountry ? 1 : 0,
                includesFuel: details.includesFuel ? 1 : 0,
            }, details.id);
        }

        promise
            .then(({ rate, message }) => {
                form.reset();
                successAlert(message);
                onUpload(rate);
                setShow(false);
            })
            .catch(e => errorAlert(e))
            .finally(() => setHandlingSubmit(false))
    }

    return (
        <Modal show={show} onExit={() => setValidated(false)} centered animation={false} size="lg">
            <Modal.Body>
                <Form noValidate validated={validated} ref={inputref}>

                    <Row className="g-1">
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Category</Label>
                            <SelectInput
                                value={[details.vehicle_category_id]}
                                onChange={([id]) => setDetails(d => ({ ...d, vehicle_category_id: id || "" }))}
                                options={vehicle_categories}
                                required
                                placeholder='-'
                                maxItems={1}
                                errorText="This field is required"
                            />
                        </Col>
                    </Row>


                    <Row className="g-1">
                        <Col sm={6} className="my-1">
                            <Label className="form-field-title">Amount</Label>
                            <Control
                                value={details.amount}
                                type="number"
                                onChange={e => setDetails({ ...details, amount: e.currentTarget.value })}
                                required
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col xs={6} className="my-1">
                            <Label className="form-field-title">Unit</Label>
                            <Form.Select
                                value={details.unit}
                                onChange={e => setDetails({ ...details, unit: e.currentTarget.value })}
                                required
                            >
                                <option value="" />
                                {billiingUnits.map(t => <option key={t} value={t}>{UCWords(t.replace(/-/g, " "))}</option>)}
                            </Form.Select>
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={6} sm={4} className="my-1">
                            <Form.Check
                                type="checkbox"
                                label={`Includes Driver`}
                                id={`includesDriver`}
                                checked={details.includesDriver}
                                onChange={() => setDetails(d => ({ ...d, includesDriver: !d.includesDriver }))}
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>

                        <Col xs={6} sm={4} className="my-1">
                            <Form.Check
                                type="checkbox"
                                label={`Includes Fuel`}
                                id={`includesFuel`}
                                checked={details.includesFuel}
                                onChange={() => setDetails(d => ({ ...d, includesFuel: !d.includesFuel }))}
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>
                        <Col xs={6} sm={4} className="my-1">
                            <Form.Check
                                type="checkbox"
                                label={`Up Country`}
                                id={`upCountry`}
                                checked={details.upCountry}
                                onChange={() => setDetails(d => ({ ...d, upCountry: !d.upCountry }))}
                            />
                            <Control.Feedback type="invalid">
                                Required
                            </Control.Feedback>
                        </Col>

                    </Row>



                    <Row>
                        <Col className="mt-4 mb-0 text-end">
                            <SubmitButton isSubmitting={handlingSubmit} onClick={handleSubmit}>
                                {action === "edit" ? "Update" : "Submit"}
                            </SubmitButton>
                            <CancelButton isSubmitting={handlingSubmit} onClick={() => setShow(false)}>
                                Cancel
                            </CancelButton>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
        </Modal >
    )
}



export default VehicleRateForm;